import numeral from 'numeral';
import type React from 'react';
import { useState } from 'react';
import { useAppSelector } from '../flux/configureStore';

// Constellation
import {
  AdornedInput,
  Adornment,
  Button,
  ButtonGroup,
  Heading,
  Input,
  Label,
  ModalDialog,
  Paragraph,
  Spacer,
  TextButton,
} from '@zillow/constellation';

// Misc / utils
import { augmentError, formatForLog } from '../utils/errors';
import { logger } from '../index';
import type { PartialFilterType } from '../models/Types';
import * as S from './styles';

const NoZestimateModal = ({
  isOpen,
  onClose,
  onSetAppError,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSetAppError: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const setFilter = useAppSelector((state) => state.setFilter);
  const [inputPrice, updateInputPrice] = useState('');
  const [errorMsg, updateErrorMsg] = useState('');
  const userProperty = useAppSelector((state) => state.address);
  const { street, unit, city, state, zip } = userProperty;
  const address = `${street}${unit && unit !== ' ' ? ', Unit ' + unit : ''}, ${city}, ${state} ${zip}`;

  const handleCancel = () => {
    updateErrorMsg('');
    onClose();
  };

  const handlePriceChange = (e: React.FormEvent<HTMLInputElement>) => {
    updateErrorMsg('');

    // @ts-ignore https://github.com/desktop/desktop/issues/297
    const enteredValue = e.target.value;

    updateInputPrice(enteredValue ? numeral(enteredValue).format('0,0') : '');
  };

  const handleSubmit = async () => {
    const price = numeral(inputPrice).value();
    if (price === null) {
      updateErrorMsg('Please enter an amount');
      return;
    }

    const partialFilterObj: PartialFilterType = { price };

    try {
      await setFilter(partialFilterObj);
      onSetAppError('');
    } catch (err) {
      augmentError(err, {
        root: 'components/ChangePriceModal#handleSave',
      });

      logger.error(formatForLog(err), 'Filter: Error handling filter price change');
    }

    onClose();
  };

  return (
    <ModalDialog
      mediaQuery={{ xs: { fullScreen: true }, lg: { fullScreen: false } }}
      onClose={handleCancel}
      isOpen={isOpen}
      header={
        <Heading level={5}>
          Rent Zestimate<sup>®</sup> not found
        </Heading>
      }
      body={
        <div>
          <Paragraph marginTop="sm" marginBottom="xs">
            We’re unable to provide a Rent Zestimate<sup>®</sup> for <strong>{`${address}`}</strong>.
          </Paragraph>
          <Paragraph marginBottom="sm">
            Enter a custom price and we&apos;ll show you nearby rental listings closest to the price you choose.
          </Paragraph>
          <Label required>Custom price</Label>
          <AdornedInput
            error={errorMsg !== ''}
            input={<Input required onChange={handlePriceChange} placeholder="Enter amount" value={inputPrice} />}
            leftAdornment={<Adornment>$</Adornment>}
            marginTop="xs"
            size="lg"
          />
          {errorMsg !== '' && <S.InputError>{errorMsg}</S.InputError>}
          <Spacer marginTop="xs" marginBottom="sm" />
        </div>
      }
      footer={
        <ButtonGroup aria-label="See listings">
          <TextButton onClick={handleCancel}>Cancel</TextButton>
          <Button buttonType="primary" onClick={handleSubmit}>
            See listings
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default NoZestimateModal;
